import { useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { api, ApiError } from '../../services/api';

interface ActivationTabProps {
  currentStatus: string;
  blockedAt: string | null;
  token: string;
  onStatusChange: () => void;
}

export default function ActivationTab({ currentStatus, blockedAt, token, onStatusChange }: ActivationTabProps) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isActive = currentStatus === 'APPROVED';
  const isBlocked = !!blockedAt;
  // #1103: the toggle only moves an approved garage between APPROVED and INACTIVE.
  const isApproved = currentStatus === 'APPROVED' || currentStatus === 'INACTIVE';

  const handleToggle = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.patch('/api/garages/me/activation', { active: !isActive }, token);
      setShowConfirm(false);
      onStatusChange();
    } catch (err) {
      console.error('Failed to toggle activation:', err);
      setShowConfirm(false);
      setError(err instanceof ApiError ? err.message : 'Could not change activation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-tab-content">
      <section className="profile-section">
        <h3 className="profile-section-title">{t('profile.activation.title')}</h3>
        <p className="profile-section-subtitle">{t('profile.activation.subtitle')}</p>

        {isBlocked && (
          <div
            style={{
              background: '#fde2e2',
              border: '1px solid #7f1d1d',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              margin: '0.75rem 0',
              color: '#7f1d1d',
              fontSize: '0.875rem',
            }}
          >
            <strong style={{ display: 'block', marginBottom: '0.125rem' }}>Blocked by admin</strong>
            Your garage is currently blocked. You cannot change your activation status until an admin unblocks you. Please contact support.
          </div>
        )}

        {!isApproved && (
          <div
            style={{
              background: '#fef3c7',
              border: '1px solid #92400e',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              margin: '0.75rem 0',
              color: '#92400e',
              fontSize: '0.875rem',
            }}
          >
            <strong style={{ display: 'block', marginBottom: '0.125rem' }}>Awaiting approval</strong>
            Your garage can be activated once Garagat has approved it.
          </div>
        )}

        {error && (
          <div role="alert" style={{ color: '#b91c1c', fontSize: '0.875rem', margin: '0.5rem 0' }}>
            {error}
          </div>
        )}

        <div className="activation-toggle">
          <button
            className={`activation-btn ${isActive ? 'active' : 'inactive'}`}
            onClick={() => setShowConfirm(true)}
            disabled={loading || isBlocked || !isApproved}
            title={isBlocked ? 'Garage is blocked by admin' : !isApproved ? 'Garage is awaiting approval' : undefined}
          >
            {isActive
              ? t('profile.activation.active')
              : t('profile.activation.inactive')}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 5l3 3 3-3" />
            </svg>
          </button>
        </div>
      </section>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="dialog-backdrop" onClick={() => setShowConfirm(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px', textAlign: 'center' }}>
            <div style={{ padding: '32px 24px 24px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: isActive ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                {isActive ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="#ef4444"/></svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="#22c55e"/></svg>
                )}
              </div>
              <h3 style={{ fontFamily: 'inherit', fontSize: '18px', fontWeight: 600, color: '#1e293b', margin: '0 0 8px' }}>
                {isActive
                  ? t('profile.activation.confirmDeactivate')
                  : t('profile.activation.confirmActivate')}
              </h3>
              <p style={{ fontFamily: 'Alexandria, sans-serif', fontSize: '14px', color: '#505050', margin: '0 0 24px' }}>
                {isActive
                  ? t('profile.activation.deactivateWarning')
                  : t('profile.activation.activateMessage')}
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button className="edit-panel-cancel" onClick={() => setShowConfirm(false)}>{t('common.cancel')}</button>
                <button
                  className="edit-panel-save"
                  onClick={handleToggle}
                  disabled={loading}
                  style={{ background: isActive ? '#ef4444' : '#22c55e' }}
                >
                  {loading ? '...' : isActive ? t('profile.activation.deactivate') : t('profile.activation.activate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
